import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// link not needed here
import {
  SG_GET_DASHBOARD_SYSTEM_OVERVIEW,
  SG_GET_DASHBOARD_ORGANIZATION_USAGE,
  SG_GET_DASHBOARD_PERFORMANCE_METRICS,
} from 'constants/actions';

export default function Dashboard() {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.action_dashboard?.dashboard || {});
  const systemOverview = useSelector((s) => s.action_dashboard?.system_overview || {});
  const orgUsage = useSelector((s) => s.action_dashboard?.organization_usage || {});
  const perf = useSelector((s) => s.action_dashboard?.performance_metrics || {});

  const [orgPage, setOrgPage] = useState(1);
  const [orgLoading, setOrgLoading] = useState(false);

  useEffect(() => {
    // fetch system overview and performance metrics on mount
    dispatch({ type: SG_GET_DASHBOARD_SYSTEM_OVERVIEW });
    dispatch({ type: SG_GET_DASHBOARD_PERFORMANCE_METRICS });
  }, [dispatch]);

  useEffect(() => {
    // request paginated organization usage
    const orgQuery = `page=${orgPage}`;
    setOrgLoading(true);
    dispatch({ type: SG_GET_DASHBOARD_ORGANIZATION_USAGE, payload: orgQuery });
  }, [dispatch, orgPage]);

  // clear loading when orgUsage updates
  useEffect(() => {
    // when orgUsage changes (results or count), assume load finished
    if (orgUsage && (orgUsage.results || orgUsage.count || Array.isArray(orgUsage))) {
      setOrgLoading(false);
    }
  }, [orgUsage]);

  const empty = !Object.keys(data).length && !Object.keys(systemOverview).length && !Object.keys(orgUsage).length && !Object.keys(perf).length;

  // helpers
  function StatCard({ title, value, subtitle, color = '#0b5fff' }) {
    return (
      <div style={{ padding: 14, borderRadius: 10, background: '#fff', border: '1px solid #eee', minWidth: 160 }}>
        <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.6 }}>{title}</div>
        <div style={{ fontSize: 28, fontWeight: 700, color, margin: '6px 0' }}>{value}</div>
        {subtitle ? <div style={{ fontSize: 12, color: '#888' }}>{subtitle}</div> : null}
      </div>
    );
  }

  function PrettyJSON({ obj }) {
    const text = JSON.stringify(obj || {}, null, 2);
    const copy = () => {
      try { if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text); } catch (e) { }
    };
    return (
      <div style={{ position: 'relative' }}>
        <button onClick={copy} style={{ position: 'absolute', right: 8, top: 8, border: 'none', background: '#0b5fff', color: '#fff', padding: '6px 8px', borderRadius: 6, cursor: 'pointer' }}>Copy</button>
        <pre aria-label="raw" style={{ background: '#fafafa', padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 320 }}>{text}</pre>
      </div>
    );
  }

  function RecentCard({ item, type = 'campaign' }) {
    const title = item?.name || item?.report_name || item?.tracking_code || item?.id;
    const meta = type === 'campaign' ? `${item.organization?.name || '-'} • ${item.status || '-'} • ${item.current_phase || '-'}` : `${item.status || '-'} • ${item.report_type || ''}`;
    return (
      <div style={{ border: '1px solid #eee', padding: 12, borderRadius: 8, background: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <div style={{ color: '#666', fontSize: 12 }}>{item.created_at ? new Date(item.created_at).toLocaleString() : ''}</div>
        </div>
        <div style={{ color: '#666', marginTop: 8 }}>{meta}</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 18 }}>
      <h2 style={{ margin: '0 0 12px 0' }}>Action Dashboard</h2>
      {empty ? (
        <div>Loading dashboard...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* top summary */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <StatCard title="Organizations" value={systemOverview.organizations?.total ?? '-'} subtitle={`Active: ${systemOverview.organizations?.active ?? '-'}`} color="#2b7a78" />
            <StatCard title="Campaigns" value={systemOverview.campaigns?.total ?? '-'} subtitle={`Active: ${systemOverview.campaigns?.active ?? '-'}`} color="#0b67ff" />
            <StatCard title="Reports" value={systemOverview.reports?.total ?? '-'} subtitle={`Pending: ${systemOverview.reports?.pending_review ?? '-'}`} color="#b36cff" />
            <StatCard title="Actions" value={systemOverview.actions?.total ?? '-'} subtitle={`Completed: ${systemOverview.actions?.completed ?? '-'}`} color="#ff6b6b" />
          </div>
          {/* Summary removed per request; System overview appears first */}

          <section style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fff' }}>
            <h3 style={{ marginTop: 0 }}>System overview</h3>
            {systemOverview && systemOverview.organizations ? (
              <>
              {/* diagnostics banner (neutral, payload-aware) */}
              {(() => {
                const warnings = [];
                const hasActiveCampaigns = systemOverview.campaigns?.active > 0;
                if (!hasActiveCampaigns) warnings.push({ level: 'info', text: 'No active campaigns in the system overview' });
                if ((systemOverview.reports?.pending_review || 0) > 0) warnings.push({ level: 'warn', text: `${systemOverview.reports.pending_review} pending report${systemOverview.reports.pending_review > 1 ? 's' : ''}` });
                if ((systemOverview.actions?.completion_rate ?? 0) < 50) warnings.push({ level: 'critical', text: `Low action completion rate (${systemOverview.actions?.completion_rate ?? 0}%)` });

                if (!warnings.length) return null;

                const bg = warnings.some(w => w.level === 'critical') ? '#fff0f0' : '#fff7e6';
                const border = warnings.some(w => w.level === 'critical') ? '#f5c2c2' : '#f7d6a5';

                return (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ padding: 12, borderRadius: 8, display: 'flex', gap: 12, alignItems: 'center', background: bg, border: `1px solid ${border}` }}>
                      <div style={{ fontSize: 20 }}>⚠️</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#333' }}>Health checks</div>
                        <div style={{ color: '#444', marginTop: 6 }}>
                          {warnings.map((w, i) => (
                            <div key={i} style={{ color: w.level === 'critical' ? '#a33' : '#7a5' }}>{w.text}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 12 }}>
                {/* Left: 2x2 cards grid */}
                <div>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
                      <div style={{ fontSize: 12, color: '#666' }}>Organizations</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{systemOverview.organizations.total}</div>
                      <div style={{ color: '#777', marginTop: 6 }}>Active: {systemOverview.organizations.active} — New this month: {systemOverview.organizations.new_this_month ?? 0}</div>
                    </div>

                    <div style={{ flex: 1, padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
                      <div style={{ fontSize: 12, color: '#666' }}>Campaigns</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{systemOverview.campaigns.total}</div>
                      <div style={{ color: '#777', marginTop: 6 }}>Active: {systemOverview.campaigns.active} — Created this month: {systemOverview.campaigns.created_this_month ?? 0}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
                      <div style={{ fontSize: 12, color: '#666' }}>Reports</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{systemOverview.reports.total}</div>
                      <div style={{ color: '#777', marginTop: 6 }}>Pending review: {systemOverview.reports.pending_review} • Sent: {systemOverview.reports.sent}</div>
                    </div>

                    <div style={{ flex: 1, padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
                      <div style={{ fontSize: 12, color: '#666' }}>Actions</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{systemOverview.actions.total}</div>
                      <div style={{ color: '#777', marginTop: 6 }}>Completed: {systemOverview.actions.completed} • Completion rate: {systemOverview.actions.completion_rate ?? 0}%</div>
                    </div>
                  </div>
                </div>

                {/* Right: recent activity split into campaigns and reports */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <h4 style={{ margin: '6px 0 6px 0' }}>Recent campaigns</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(systemOverview.recent_activity?.campaigns || []).slice(0, 5).map((c) => (
                        <RecentCard key={c.id} item={c} type="campaign" />
                      ))}
                      {!systemOverview.recent_activity?.campaigns?.length ? <div style={{ color: '#666' }}>No recent campaigns</div> : null}
                    </div>
                  </div>

                  <div>
                    <h4 style={{ margin: '6px 0 6px 0' }}>Recent reports</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(systemOverview.recent_activity?.reports || []).slice(0, 5).map((r) => (
                        <RecentCard key={r.id} item={r} type="report" />
                      ))}
                      {!systemOverview.recent_activity?.reports?.length ? <div style={{ color: '#666' }}>No recent reports</div> : null}
                    </div>
                  </div>
                </div>
              </div>
              </>
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(systemOverview, null, 2)}</pre>
            )}
          </section>

          {/* Diagnostics & Raw payload */}
          <section style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fff' }}>
            <h3 style={{ marginTop: 0 }}>Diagnostics & raw data</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 12 }}>
              <div>
                <div style={{ color: '#333', marginBottom: 8 }}>Quick summary</div>
                <div style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: '#666' }}>Total organizations</div>
                      <div style={{ fontWeight: 700 }}>{systemOverview.organizations?.total ?? '-'}</div>
                      <div style={{ color: '#666', marginTop: 8 }}>Campaigns created this month: {systemOverview.campaigns?.created_this_month ?? 0}</div>
                    </div>
                    <div style={{ width: 1, background: '#eee' }} />
                    <div style={{ width: 200 }}>
                      <div style={{ fontSize: 12, color: '#666' }}>Avg report generation</div>
                      <div style={{ fontWeight: 700 }}>{perf.performance?.avg_report_generation_time ?? '-'}s</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>API RT: {perf.performance?.api_response_time ?? '-'}ms</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ color: '#333', fontWeight: 700, marginBottom: 6 }}>Recent activity (full JSON)</div>
                  <div style={{ padding: 8, border: '1px solid #eee', borderRadius: 8 }}>
                    <PrettyJSON obj={systemOverview.recent_activity} />
                  </div>
                </div>
              </div>

              <div>
                <div style={{ color: '#333', fontWeight: 700, marginBottom: 6 }}>Full payload (systemOverview)</div>
                <PrettyJSON obj={systemOverview} />
              </div>
            </div>
          </section>

          <section style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fff' }}>
            <h3 style={{ marginTop: 0 }}>Organization usage</h3>
            {orgUsage?.results || Array.isArray(orgUsage) ? (
              <div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ borderBottom: '1px solid #ddd', padding: 8 }}>Organization</th>
                      <th style={{ borderBottom: '1px solid #ddd', padding: 8, textAlign: 'center' }}>Campaigns</th>
                      <th style={{ borderBottom: '1px solid #ddd', padding: 8, textAlign: 'center' }}>Managers</th>
                      <th style={{ borderBottom: '1px solid #ddd', padding: 8, textAlign: 'center' }}>Actions</th>
                      <th style={{ borderBottom: '1px solid #ddd', padding: 8, textAlign: 'center' }}>Completion %</th>
                      <th style={{ borderBottom: '1px solid #ddd', padding: 8, textAlign: 'center' }}>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orgUsage?.results || orgUsage).map((o, i) => (
                      <tr key={o.organization?.id || i}>
                        <td style={{ padding: 8 }}>{o.organization?.name || '-'}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{o.campaigns_count ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{o.managers_count ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{o.actions_count ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{o.completion_rate ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{o.last_activity ? new Date(o.last_activity).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={() => { setOrgLoading(true); setOrgPage(Math.max(1, orgPage - 1)); }} disabled={!orgUsage?.previous || orgLoading}>Previous</button>
                  <span style={{ margin: '0 8px' }}>
                    Page {orgPage} of {orgUsage?.total_pages ?? (orgUsage?.count ? Math.ceil((orgUsage?.count || 0) / (orgUsage?.page_size || 50)) : '-')}
                  </span>
                  <button onClick={() => { setOrgLoading(true); setOrgPage(orgPage + 1); }} disabled={!orgUsage?.next || orgLoading}>Next</button>

                  <div style={{ marginLeft: 'auto', fontSize: 13, color: '#333', display: 'flex', alignItems: 'center', gap: 12 }}>
                    {orgLoading ? <span style={{ color: '#888' }}>Loading...</span> : null}
                    <div> Total: <strong>{orgUsage?.count ?? (orgUsage?.length || 0)}</strong></div>
                    <div style={{ marginLeft: 12 }}>Page size: <strong>{orgUsage?.page_size ?? '-'}</strong></div>
                  </div>
                </div>
              </div>
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(orgUsage, null, 2)}</pre>
            )}
          </section>

          <section style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fff' }}>
            <h3 style={{ marginTop: 0 }}>Performance metrics</h3>
            {perf && (perf.performance || perf.database_metrics || perf.growth_trends) ? (
              <div>
                {perf.performance && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                    {Object.entries(perf.performance).map(([k, v]) => (
                      <div key={k} style={{ flex: '1 1 180px', padding: 12, background: '#fafafa', border: '1px solid #eee', borderRadius: 8 }}>
                        <div style={{ fontSize: 12, color: '#666', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{typeof v === 'number' ? v : String(v)}</div>
                      </div>
                    ))}
                  </div>
                )}

                {perf.database_metrics && (
                  <div style={{ marginBottom: 12 }}>
                    <h4 style={{ margin: '6px 0' }}>Database metrics</h4>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {Object.entries(perf.database_metrics).map(([k, v]) => (
                        <div key={k} style={{ padding: 8, background: '#fff', border: '1px solid #eee', borderRadius: 8, minWidth: 150 }}><strong>{k.replace(/_/g, ' ')}:</strong> {String(v)}</div>
                      ))}
                    </div>
                  </div>
                )}

                {perf.growth_trends && perf.growth_trends.length > 0 && (
                  <div>
                    <h4 style={{ margin: '6px 0' }}>Growth trends (last 12 months)</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ borderBottom: '1px solid #ddd', padding: 6 }}>Month</th>
                          <th style={{ borderBottom: '1px solid #ddd', padding: 6 }}>Campaigns created</th>
                          <th style={{ borderBottom: '1px solid #ddd', padding: 6 }}>Actions completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perf.growth_trends.map((g) => (
                          <tr key={g.month}>
                            <td style={{ padding: 6 }}>{g.month}</td>
                            <td style={{ padding: 6 }}>{g.campaigns_created}</td>
                            <td style={{ padding: 6 }}>{g.actions_completed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div>No performance metrics available</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
